// ./src/components/features/FormFields/FormFieldTypeSelector/FormFieldTypeSelector.tsx
import React, { useMemo, useEffect } from "react";
import {
  Select,
  SelectProps,
  Group,
  Text,
  Stack,
  Box,
  Badge,
  Tooltip,
} from "@mantine/core";
import { IconLock } from "@tabler/icons-react";

import { FormFieldType } from "@/utils/api";
import { useFormFieldTypesContext, useApp } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@components/generic";
import { FEATURE_CODES, FIELD_TYPE_IDS } from "@/config";
import { useNumericFeature } from "@/hooks";

export interface FormFieldTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Currently selected form field type ID */
  value?: string | null | undefined;

  /** Callback fired when form field type selection changes */
  onChange?: (
    formFieldTypeId: string | null,
    formFieldType?: FormFieldType
  ) => void;

  /** Placeholder text when no form field type is selected */
  placeholder?: string;

  /** Whether to include inactive form field types in the list */
  includeInactive?: boolean;

  /** Custom filter function to filter form field types */
  formFieldTypeFilter?: (formFieldType: FormFieldType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no form field types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | number | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show description as hint below the input */
  showDescriptionHint?: boolean;
}

interface FormFieldTypeOptionData {
  value: string;
  label: string;
  description?: string;
  iconName?: string;
  disabled?: boolean;
  isPremiumLocked?: boolean;
  premiumFeatureName?: string;
}

// Map field type IDs to their premium feature requirements
const PREMIUM_FIELD_TYPE_CONFIG: Record<
  number,
  { featureCode: string; featureName: string; checkType: "boolean" | "numeric" }
> = {
  [FIELD_TYPE_IDS.FILE]: {
    featureCode: FEATURE_CODES.FILES_UPLOAD,
    featureName: "File Uploads",
    checkType: "boolean",
  },
  [FIELD_TYPE_IDS.COORDINATES]: {
    featureCode: FEATURE_CODES.GEOFENCING,
    featureName: "Geofencing",
    checkType: "boolean",
  },
  [FIELD_TYPE_IDS.LOOKUP]: {
    featureCode: FEATURE_CODES.MAX_LOOKUP_DICTIONARIES,
    featureName: "Lookup Dictionaries",
    checkType: "numeric",
  },
};

export const FormFieldTypeSelector: React.FC<FormFieldTypeSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a field type",
  includeInactive = false,
  formFieldTypeFilter,
  required = false,
  loadingPlaceholder = "Loading field types...",
  emptyText = "No field types available",
  error,
  disabled,
  size = "md",
  showDescriptionHint = true,
  ...selectProps
}) => {
  const { formFieldTypes, loading, isEmpty, lastError } =
    useFormFieldTypesContext();
  const { chechAppPlanFeature } = useApp();

  // Check premium feature availability
  const filesUploadEnabled = chechAppPlanFeature(
    FEATURE_CODES.FILES_UPLOAD,
    true
  ).didSucceed();

  const geofencingEnabled = chechAppPlanFeature(
    FEATURE_CODES.GEOFENCING,
    true
  ).didSucceed();

  const { maxValue: maxLookupDictionaries } = useNumericFeature(
    FEATURE_CODES.MAX_LOOKUP_DICTIONARIES
  );
  const lookupEnabled = maxLookupDictionaries > 0;

  // Helper to check if a field type is premium-locked based on its numeric ID
  const getPremiumLockStatus = (
    fieldTypeId: string | number | undefined
  ): { isLocked: boolean; featureName: string | undefined } => {
    if (fieldTypeId === undefined || fieldTypeId === null) {
      return { isLocked: false, featureName: undefined };
    }

    const numericId =
      typeof fieldTypeId === "string" ? parseInt(fieldTypeId, 10) : fieldTypeId;

    if (isNaN(numericId)) {
      return { isLocked: false, featureName: undefined };
    }

    const config = PREMIUM_FIELD_TYPE_CONFIG[numericId];
    if (!config) {
      return { isLocked: false, featureName: undefined };
    }

    let isLocked = false;

    switch (numericId) {
      case FIELD_TYPE_IDS.FILE:
        isLocked = !filesUploadEnabled;
        break;
      case FIELD_TYPE_IDS.COORDINATES:
        isLocked = !geofencingEnabled;
        break;
      case FIELD_TYPE_IDS.LOOKUP:
        isLocked = !lookupEnabled;
        break;
    }

    return { isLocked, featureName: isLocked ? config.featureName : undefined };
  };

  // Filter and prepare form field type options
  const formFieldTypeOptions = useMemo((): FormFieldTypeOptionData[] => {
    let filteredFormFieldTypes = formFieldTypes;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredFormFieldTypes = filteredFormFieldTypes.filter(
        (formFieldType) => formFieldType.status === 100
      );
    }

    // Apply custom filter if provided
    if (formFieldTypeFilter) {
      filteredFormFieldTypes =
        filteredFormFieldTypes.filter(formFieldTypeFilter);
    }

    // Convert to select options with additional data
    return filteredFormFieldTypes.map((formFieldType) => {
      const { isLocked, featureName } = getPremiumLockStatus(formFieldType.id);

      return {
        value: formFieldType.id || "",
        label: formFieldType.name || "Unnamed Field Type",
        description: formFieldType.description,
        iconName: formFieldType.iconName,
        // Disable if status is 0 OR if it's a premium-locked feature
        disabled: formFieldType.status === 0 || isLocked,
        isPremiumLocked: isLocked,
        premiumFeatureName: featureName,
      };
    });
  }, [
    formFieldTypes,
    includeInactive,
    formFieldTypeFilter,
    filesUploadEnabled,
    geofencingEnabled,
    lookupEnabled,
  ]);

  // Create a map for quick form field type lookup by ID
  const formFieldTypeMap = useMemo(() => {
    const map = new Map<string, FormFieldType>();
    formFieldTypes.forEach((formFieldType) => {
      if (formFieldType.id) {
        map.set(formFieldType.id, formFieldType);
      }
    });
    return map;
  }, [formFieldTypes]);

  // Get the currently selected form field type
  const selectedFormFieldType = useMemo(() => {
    if (!value) return null;
    return formFieldTypeMap.get(value) || null;
  }, [value, formFieldTypeMap]);

  // Auto-select first non-disabled form field type if required and no value is set
  useEffect(() => {
    if (
      required &&
      !value &&
      formFieldTypeOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      // Find first non-disabled option
      const firstAvailable = formFieldTypeOptions.find(
        (option) => !option.disabled
      );
      if (firstAvailable) {
        const selectedType = formFieldTypeMap.get(firstAvailable.value);
        onChange(firstAvailable.value, selectedType);
      }
    }
  }, [
    required,
    value,
    formFieldTypeOptions,
    loading,
    onChange,
    formFieldTypeMap,
  ]);

  // Handle onChange with form field type object lookup
  const handleChange = (selectedFormFieldTypeId: string | null) => {
    if (selectedFormFieldTypeId) {
      const selectedType = formFieldTypeMap.get(selectedFormFieldTypeId);
      onChange?.(selectedFormFieldTypeId, selectedType);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Render option for dropdown (shows icon, name, description, and premium badge)
  const renderOption: SelectProps["renderOption"] = ({ option }) => {
    const optionData = option as FormFieldTypeOptionData;

    return (
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Box pt={2}>
          <AppIcon iconName={optionData.iconName || "QuestionMark"} size={20} />
        </Box>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text
              size="sm"
              fw={500}
              //c={optionData.isPremiumLocked ? "dimmed" : undefined}
            >
              {optionData.label}
            </Text>
            {optionData.isPremiumLocked && (
              <Tooltip
                label={`Upgrade your plan to use ${
                  optionData.premiumFeatureName || "this feature"
                }`}
                withArrow
              >
                <Badge
                  size="xs"
                  variant="light"
                  color="yellow"
                  leftSection={<IconLock size={10} />}
                >
                  Premium
                </Badge>
              </Tooltip>
            )}
          </Group>
          {optionData.description && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {optionData.description}
            </Text>
          )}
        </Stack>
      </Group>
    );
  };

  // Render selected item icon
  const renderSelectedIcon = () => {
    if (!selectedFormFieldType?.iconName) return undefined;
    return <AppIcon iconName={selectedFormFieldType.iconName} size={18} />;
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    if (loading && formFieldTypes.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading field types",
        disabled: true,
        error: error || "Failed to load field types",
      };
    }

    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    return {
      data: formFieldTypeOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && formFieldTypes.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  return (
    <Box>
      <Select
        {...selectProps}
        size={size}
        value={value ?? null}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        searchable
        required={required}
        disabled={selectState.disabled}
        error={selectState.error || error}
        data={selectState.data}
        rightSection={selectState.rightSection}
        leftSection={renderSelectedIcon()}
        renderOption={renderOption}
      />
      {showDescriptionHint && selectedFormFieldType?.description && !error && (
        <Text size="xs" c="dimmed" mt={4} px={2}>
          {selectedFormFieldType.description}
        </Text>
      )}
    </Box>
  );
};
